import {
  AppstoreOutlined,
  BellOutlined,
  CommentOutlined,
  GiftOutlined,
  InboxOutlined,
  PictureOutlined,
  BarChartOutlined,
  DashboardOutlined,
  DownOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  TeamOutlined,
  TrademarkOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  ReadOutlined,
  FormOutlined,
  MailOutlined,
  BookOutlined,
  EnvironmentOutlined,
  FlagOutlined,
  GlobalOutlined,
  PartitionOutlined,
} from '@ant-design/icons';
import { Button, Layout, Menu, theme } from 'antd';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ScrollToTop } from '@/components/ScrollToTop/ScrollToTop';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { UserProfileDropdown } from '@/components/UserProfileDropdown/UserProfileDropdown';
import { GlobalLoader } from '@/components/GlobalLoader/GlobalLoader';
import { APP_NAME, LOGO_PATH } from '@/constants/brand';
import { ROUTES } from '@/constants/routes';
import { getModuleKeyForPath, MENU_GROUP_MODULES } from '@/constants/adminModules';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { useGetUserDetailQuery } from '@/modules/user/userApi';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logout } from '@/store/authSlice';
import { resetGlobalLoading, toggleSidebar } from '@/store/uiSlice';
import type { User } from '@/types/auth';
import { getUserDisplayName, getUserRoleLabel } from '@/utils/user';
import { hasAnyModuleAccess, hasModuleAccess } from '@/utils/permissions';
import { resolveUserAvatarUrl } from '@/utils/media';
import styles from './MainLayout.module.scss';

const { Header, Sider, Content } = Layout;

const FACTBOOK_MENU_KEY = 'factbook';

const NON_NAVIGABLE_MENU_KEYS = new Set<string>([FACTBOOK_MENU_KEY]);

const EXPANDED_MENU_ITEMS = [
  { key: ROUTES.DASHBOARD, icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: ROUTES.ORDERS, icon: <ShoppingCartOutlined />, label: 'Orders' },
  { key: ROUTES.CUSTOMERS, icon: <TeamOutlined />, label: 'Customers' },
  { key: ROUTES.ENQUIRIES, icon: <FormOutlined />, label: 'Enquiries' },
  { key: ROUTES.CONTACTS, icon: <MailOutlined />, label: 'Contact Us' },
  { key: ROUTES.NOTIFICATIONS, icon: <BellOutlined />, label: 'Notifications' },
  {
    key: 'catalog',
    icon: <AppstoreOutlined />,
    label: 'Catalog',
    children: [
      { key: ROUTES.CATEGORIES, label: 'Category Manager' },
      // { key: ROUTES.SUBCATEGORIES, label: 'Subcategories' },
      { key: ROUTES.BRANDS, label: 'Brands' },
      { key: ROUTES.STATES, label: 'States' },
      { key: ROUTES.DISTRICTS, label: 'Districts' },
      { key: ROUTES.ASSEMBLIES, label: 'Assemblies' },
      { key: ROUTES.CONSTITUENCIES, label: 'Constituencies' },
      { key: ROUTES.ATTRIBUTES, label: 'Attributes' },
      { key: ROUTES.ATTRIBUTE_VALUES, label: 'Sub Attributes' },
      { key: ROUTES.PRODUCTS, label: 'Products' },
      { key: ROUTES.INVENTORY, label: 'Inventory' },
      { key: ROUTES.REVIEWS, label: 'Reviews' },
    ],
  },
  {
    key: 'content',
    icon: <PictureOutlined />,
    label: 'Content',
    children: [
      // { key: ROUTES.BANNERS, label: 'Banners' },
      { key: ROUTES.COLLECTIONS, label: 'Collections' },
      // Hidden from sidebar — media still used via MediaPicker on product/forms
      // { key: ROUTES.MEDIA, label: 'Media Manager' },
      { key: ROUTES.CMS, label: 'CMS Manager' },
      { key: ROUTES.SEO, label: 'SEO Manager' },
      { key: ROUTES.EMAIL_TEMPLATES, label: 'Email Template Manager' },
      { key: ROUTES.BLOGS, label: 'Blog Manager' },
      { key: FACTBOOK_MENU_KEY, label: 'Factbook' },
    ],
  },
  {
    key: 'sales',
    icon: <GiftOutlined />,
    label: 'Sales',
    children: [
      { key: ROUTES.COUPONS, label: 'Coupons' },
      { key: ROUTES.REPORTS, label: 'Reports' },
    ],
  },
  { key: ROUTES.USERS, icon: <SafetyCertificateOutlined />, label: 'User & Role Manager' },
  { key: ROUTES.SETTINGS, icon: <SettingOutlined />, label: 'Settings' },
];

const COLLAPSED_MENU_ITEMS = [
  { key: ROUTES.DASHBOARD, icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: ROUTES.ORDERS, icon: <ShoppingCartOutlined />, label: 'Orders' },
  { key: ROUTES.CUSTOMERS, icon: <TeamOutlined />, label: 'Customers' },
  { key: ROUTES.ENQUIRIES, icon: <FormOutlined />, label: 'Enquiries' },
  { key: ROUTES.CONTACTS, icon: <MailOutlined />, label: 'Contact Us' },
  { key: ROUTES.NOTIFICATIONS, icon: <BellOutlined />, label: 'Notifications' },
  { key: ROUTES.CATEGORIES, icon: <TagsOutlined />, label: 'Category Manager' },
  // { key: ROUTES.SUBCATEGORIES, icon: <TagsOutlined />, label: 'Subcategories' },
  { key: ROUTES.BRANDS, icon: <TrademarkOutlined />, label: 'Brands' },
  { key: ROUTES.STATES, icon: <GlobalOutlined />, label: 'States' },
  { key: ROUTES.DISTRICTS, icon: <EnvironmentOutlined />, label: 'Districts' },
  { key: ROUTES.ASSEMBLIES, icon: <PartitionOutlined />, label: 'Assemblies' },
  { key: ROUTES.CONSTITUENCIES, icon: <FlagOutlined />, label: 'Constituencies' },
  { key: ROUTES.ATTRIBUTES, icon: <TagsOutlined />, label: 'Attributes' },
  { key: ROUTES.ATTRIBUTE_VALUES, icon: <UnorderedListOutlined />, label: 'Sub Attributes' },
  { key: ROUTES.PRODUCTS, icon: <ShoppingOutlined />, label: 'Products' },
  { key: ROUTES.INVENTORY, icon: <InboxOutlined />, label: 'Inventory' },
  { key: ROUTES.REVIEWS, icon: <CommentOutlined />, label: 'Reviews' },
  // { key: ROUTES.BANNERS, icon: <PictureOutlined />, label: 'Banners' },
  { key: ROUTES.COLLECTIONS, icon: <AppstoreOutlined />, label: 'Collections' },
  // Hidden from sidebar — media still used via MediaPicker on product/forms
  // { key: ROUTES.MEDIA, icon: <PictureOutlined />, label: 'Media Manager' },
  { key: ROUTES.CMS, icon: <FileTextOutlined />, label: 'CMS Manager' },
  { key: ROUTES.SEO, icon: <FileTextOutlined />, label: 'SEO Manager' },
  { key: ROUTES.EMAIL_TEMPLATES, icon: <MailOutlined />, label: 'Email Template Manager' },
  { key: ROUTES.BLOGS, icon: <ReadOutlined />, label: 'Blog Manager' },
  { key: FACTBOOK_MENU_KEY, icon: <BookOutlined />, label: 'Factbook' },
  { key: ROUTES.COUPONS, icon: <GiftOutlined />, label: 'Coupons' },
  { key: ROUTES.REPORTS, icon: <BarChartOutlined />, label: 'Reports' },
  { key: ROUTES.USERS, icon: <SafetyCertificateOutlined />, label: 'User & Role Manager' },
  { key: ROUTES.SETTINGS, icon: <SettingOutlined />, label: 'Settings' },
];

const CONTENT_ROUTES: string[] = [
  // ROUTES.BANNERS,
  ROUTES.COLLECTIONS,
  // ROUTES.MEDIA, // sidebar item commented out
  ROUTES.CMS,
  ROUTES.SEO,
  ROUTES.EMAIL_TEMPLATES,
  ROUTES.BLOGS,
  ROUTES.BLOG_CREATE,
];

function isContentPath(pathname: string): boolean {
  return CONTENT_ROUTES.includes(pathname) || /^\/blogs\/[^/]+\/edit$/.test(pathname);
}

const SALES_ROUTES: string[] = [ROUTES.COUPONS, ROUTES.COUPON_CREATE, ROUTES.REPORTS];

function isSalesPath(pathname: string): boolean {
  return SALES_ROUTES.includes(pathname) || /^\/coupons\/[^/]+\/edit$/.test(pathname);
}

const CATALOG_ROUTES: string[] = [
  ROUTES.CATEGORIES,
  // ROUTES.SUBCATEGORIES,
  ROUTES.BRANDS,
  ROUTES.STATES,
  ROUTES.DISTRICTS,
  ROUTES.ASSEMBLIES,
  ROUTES.ATTRIBUTES,
  ROUTES.ATTRIBUTE_VALUES,
  ROUTES.PRODUCTS,
  ROUTES.INVENTORY,
  ROUTES.REVIEWS,
];

type LayoutMenuItem = {
  key: string;
  icon?: ReactNode;
  label: string;
  children?: { key: string; label: string }[];
};

function filterMenuItems(items: LayoutMenuItem[], user: User | null | undefined) {
  return items
    .map((item) => {
      if ('children' in item && item.children) {
        const groupKey = item.key as keyof typeof MENU_GROUP_MODULES;
        const groupModules = MENU_GROUP_MODULES[groupKey];
        if (groupModules && !hasAnyModuleAccess(user, groupModules)) {
          return null;
        }

        const children = item.children.filter((child) => {
          const moduleKey = getModuleKeyForPath(child.key as string);
          return moduleKey ? hasModuleAccess(user, moduleKey) : true;
        });

        if (!children.length) return null;
        return { ...item, children };
      }

      const moduleKey = getModuleKeyForPath(item.key as string);
      if (moduleKey && !hasModuleAccess(user, moduleKey)) return null;
      return item;
    })
    .filter((item): item is LayoutMenuItem => item !== null);
}

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isCatalogRoute = CATALOG_ROUTES.includes(location.pathname);
  const isContentRoute = isContentPath(location.pathname);
  const isSalesRoute = isSalesPath(location.pathname);
  const [menuOpenKeys, setMenuOpenKeys] = useState<string[]>([]);
  const contentInnerRef = useRef<HTMLDivElement>(null);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);
  const dispatch = useAppDispatch();
  const collapsed = useAppSelector((state) => state.ui.sidebarCollapsed);
  const authUser = useAppSelector((state) => state.auth.user);
  const { data: userDetail } = useGetUserDetailQuery();
  const user = userDetail ?? authUser;
  const resolvedTheme = useResolvedTheme();
  const { token } = theme.useToken();

  const profileName = getUserDisplayName(user);
  const userRole = getUserRoleLabel(user?.role);
  const avatarUrl = resolveUserAvatarUrl(user);

  const handleLogout = () => {
    dispatch(logout());
    navigate(ROUTES.LOGIN, { replace: true });
  };

  useEffect(() => {
    dispatch(resetGlobalLoading());
  }, [dispatch]);

  useEffect(() => {
    setScrollContainer(contentInnerRef.current);
  }, [location.pathname]);

  useEffect(() => {
    if (collapsed) {
      setMenuOpenKeys([]);
      return;
    }

    setMenuOpenKeys(
      collapsed
        ? []
        : isCatalogRoute
          ? ['catalog']
          : isContentRoute
            ? ['content']
            : isSalesRoute
              ? ['sales']
              : [],
    );
  }, [collapsed, isCatalogRoute, isContentRoute, isSalesRoute]);

  const menuItems = useMemo(() => {
    const items = (collapsed ? COLLAPSED_MENU_ITEMS : EXPANDED_MENU_ITEMS) as LayoutMenuItem[];
    return filterMenuItems(items, user);
  }, [collapsed, user]);

  const selectedMenuKey = useMemo(() => {
    if (isSalesPath(location.pathname)) return ROUTES.COUPONS;
    if (location.pathname.startsWith('/orders')) return ROUTES.ORDERS;
    if (location.pathname.startsWith('/products')) return ROUTES.PRODUCTS;
    if (location.pathname.startsWith('/customers')) return ROUTES.CUSTOMERS;
    if (location.pathname.startsWith('/blogs')) return ROUTES.BLOGS;
    if (location.pathname.startsWith('/cms')) return ROUTES.CMS;
    if (location.pathname.startsWith('/seo')) return ROUTES.SEO;
    if (location.pathname.startsWith('/email-templates')) return ROUTES.EMAIL_TEMPLATES;
    if (location.pathname.startsWith('/reviews')) return ROUTES.REVIEWS;
    return location.pathname;
  }, [location.pathname]);

  const handleMenuOpenChange = (keys: string[]) => {
    if (collapsed) {
      setMenuOpenKeys([]);
      return;
    }

    setMenuOpenKeys(keys);
  };

  return (
    <Layout
      className={styles.layout}
      data-theme={resolvedTheme}
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <GlobalLoader />
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={260}
        collapsedWidth={72}
        className={styles.sider}
        theme="dark"
      >
        <div className={styles.siderTop}>
          <div className={styles.logoStrip}>
            <img src={LOGO_PATH} alt={APP_NAME} className={styles.logo} />
          </div>

          {!collapsed ? <div className={styles.menuLabel}>Navigation</div> : null}
        </div>

        <div className={styles.menuWrap}>
          <Menu
            theme="dark"
            mode="inline"
            inlineCollapsed={collapsed}
            triggerSubMenuAction="click"
            selectedKeys={[selectedMenuKey]}
            openKeys={collapsed ? [] : menuOpenKeys}
            onOpenChange={handleMenuOpenChange}
            items={menuItems}
            className={styles.menu}
            onClick={({ key }) => {
              if (NON_NAVIGABLE_MENU_KEYS.has(key)) return;
              navigate(key);
            }}
          />
        </div>
      </Sider>

      <Layout className={styles.mainColumn}>
        <Header className={styles.header} style={{ background: token.colorBgContainer }}>
          <Button
            type="text"
            className={styles.headerMenuToggle}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => dispatch(toggleSidebar())}
          />

          <UserProfileDropdown
            theme={resolvedTheme}
            userName={profileName}
            userEmail={user?.email ?? ''}
            userRole={userRole}
            avatarUrl={avatarUrl}
            onSignOut={handleLogout}
          >
            {(open) => (
              <button type="button" className={styles.userProfile}>
                <PreviewImage
                  src={avatarUrl}
                  alt={profileName}
                  size={40}
                  shape="circle"
                  stopPropagation
                  fallback={profileName.charAt(0).toUpperCase()}
                />
                <span className={styles.userMeta}>
                  <span className={styles.userName}>{profileName}</span>
                  <span className={styles.userRole}>{userRole}</span>
                </span>
                <span
                  className={`${styles.profileChevron} ${open ? styles.profileChevronOpen : ''}`}
                >
                  <DownOutlined />
                </span>
              </button>
            )}
          </UserProfileDropdown>
        </Header>

        <Content className={styles.content}>
          <div ref={contentInnerRef} className={styles.contentInner}>
            <Outlet />
          </div>
          <ScrollToTop scrollContainer={scrollContainer} />
        </Content>
      </Layout>
    </Layout>
  );
}
